import React, { useState, useEffect } from 'react';
import { Brain, Save, Loader, X } from 'lucide-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';

interface AISettingsData {
  industries: string[];
  businessType: string;
  businessDescription: string;
  projectDescriptionInstructions?: string;
}

export const AISettings: React.FC = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<AISettingsData>({
    industries: ['Construction'],
    businessType: 'Small Business',
    businessDescription: ''
  });
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const availableIndustries = [
    'Construction', 'Plumbing', 'Electrical', 'HVAC', 'Landscaping',
    'Carpentry', 'Painting', 'Roofing', 'Flooring', 'Concrete',
    'General Contracting', 'Other'
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      
      if (!userStr) {
        throw new Error('No user data found. Please log in again.');
      }

      const user = JSON.parse(userStr);
      const accountId = user.currentAccountId || user.accountId;

      if (!accountId) {
        throw new Error('No account ID found. Please log in again.');
      }

      const response = await fetch(`${apiUrl}/api/accounts/${accountId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch settings');
      }

      if (data.success && data.account) {
        const aiSettings = data.account.aiSettings || {};
        
        // Handle both old single industry and new multiple industries format
        let industries = aiSettings.industries || [];
        if (industries.length === 0 && aiSettings.industry) {
          industries = [aiSettings.industry];
        }
        if (industries.length === 0) {
          industries = ['Construction'];
        }

        setSettings({
          industries,
          businessType: aiSettings.businessType || 'Small Business',
          businessDescription: aiSettings.businessDescription || '',
          projectDescriptionInstructions: aiSettings.projectDescriptionInstructions || ''
        });
      }
    } catch (err) {
      console.error('Fetch settings error:', err);
      setMessage({ 
        type: 'error', 
        text: err instanceof Error ? err.message : t('admin.failedToLoadAISettings') 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      
      if (!userStr) {
        throw new Error('No user data found. Please log in again.');
      }

      const user = JSON.parse(userStr);
      const accountId = user.currentAccountId || user.accountId;

      if (!accountId) {
        throw new Error('No account ID found. Please log in again.');
      }

      const response = await fetch(`${apiUrl}/api/accounts/${accountId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          aiSettings: settings
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to save settings');
      }

      if (data.success) {
        setMessage({ type: 'success', text: t('admin.aiSettingsSavedSuccessfully') });
        setTimeout(() => setMessage(null), 3000);
      } else {
        throw new Error(data.message || 'Failed to save settings');
      }
    } catch (err) {
      console.error('Save settings error:', err);
      setMessage({ 
        type: 'error', 
        text: err instanceof Error ? err.message : t('admin.failedToSaveAISettings') 
      });
    } finally {
      setSaving(false);
    }
  };

  const addIndustry = () => {
    if (selectedIndustry && !settings.industries.includes(selectedIndustry)) {
      setSettings({
        ...settings,
        industries: [...settings.industries, selectedIndustry]
      });
      setSelectedIndustry('');
    }
  };

  const removeIndustry = (industry: string) => {
    if (settings.industries.length > 1) {
      setSettings({
        ...settings,
        industries: settings.industries.filter(i => i !== industry)
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-accent-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Brain className="w-8 h-8 text-accent-600" />
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('admin.aiQuoteGenerationSettings')}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('admin.configureContextForAI')}
          </p>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-50 dark:bg-gray-700 dark:border dark:border-green-800/50 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200'
            : 'bg-red-50 dark:bg-gray-700 dark:border dark:border-red-800/50 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* Settings Form */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-6">
        {/* Industries */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('admin.industriesSelectAll')}
          </label>
          
          {/* Selected Industries */}
          <div className="flex flex-wrap gap-2 mb-3">
            {settings.industries.map(industry => (
              <span
                key={industry}
                className="inline-flex items-center gap-1 px-3 py-1 bg-accent-100 dark:bg-accent-900/30 text-accent-800 dark:text-accent-200 rounded-full text-sm"
              >
                {industry}
                <button
                  onClick={() => removeIndustry(industry)}
                  disabled={settings.industries.length === 1}
                  className="ml-1 hover:bg-accent-200 dark:hover:bg-accent-800/50 rounded-full p-0.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title={settings.industries.length === 1 ? t('admin.atLeastOneIndustryRequired') : t('admin.remove')}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>

          {/* Add Industry */}
          <div className="flex gap-2">
            <select
              value={selectedIndustry}
              onChange={(e) => setSelectedIndustry(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">{t('admin.selectAnIndustry')}</option>
              {availableIndustries
                .filter(ind => !settings.industries.includes(ind))
                .map(industry => (
                  <option key={industry} value={industry}>{industry}</option>
                ))
              }
            </select>
            <button
              onClick={addIndustry}
              disabled={!selectedIndustry}
              className="px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('admin.add')}
            </button>
          </div>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {t('admin.aiWillUseTerminology')}
          </p>
        </div>

        {/* Business Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('admin.businessType')}
          </label>
          <select
            value={settings.businessType}
            onChange={(e) => setSettings({ ...settings, businessType: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="Small Business">{t('admin.smallBusiness')}</option>
            <option value="Medium Business">{t('admin.mediumBusiness')}</option>
            <option value="Enterprise">{t('admin.enterprise')}</option>
            <option value="Sole Proprietor">{t('admin.soleProprietor')}</option>
            <option value="Partnership">{t('admin.partnership')}</option>
            <option value="Corporation">{t('admin.corporation')}</option>
          </select>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {t('admin.helpsAIUnderstandScale')}
          </p>
        </div>

        {/* Business Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('admin.businessDescriptionOptional')}
          </label>
          <textarea
            value={settings.businessDescription}
            onChange={(e) => setSettings({ ...settings, businessDescription: e.target.value })}
            placeholder={t('admin.businessDescriptionPlaceholder')}
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {t('admin.provideContextForPersonalizedQuotes')}
          </p>
        </div>

        {/* Project Description Instructions */}
        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('admin.projectDescriptionInstructions')}
          </label>
          <textarea
            value={settings.projectDescriptionInstructions || ''}
            onChange={(e) => setSettings({ ...settings, projectDescriptionInstructions: e.target.value })}
            placeholder={t('admin.projectDescriptionInstructionsPlaceholder')}
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {t('admin.projectDescriptionInstructionsHelp')}
          </p>
        </div>

        {/* Info Box */}
        <div className="p-4 bg-accent-50 dark:bg-gray-700 dark:border dark:border-accent-800/50 border border-accent-200 dark:border-accent-800 rounded-lg">
          <p className="text-sm text-accent-800 dark:text-accent-200">
            <strong>{t('admin.howItWorks')}</strong> {t('admin.howItWorksDescription')}
          </p>
          <ul className="mt-2 text-sm text-accent-700 dark:text-accent-300 list-disc list-inside space-y-1">
            <li>{t('admin.applyIndustryKnowledge')}</li>
            <li>{t('admin.useAppropriatePricing')}</li>
            <li>{t('admin.generateMatchingQuotes')}</li>
            <li>{t('admin.understandBusinessCapabilities')}</li>
          </ul>
          <p className="mt-3 text-xs text-accent-600 dark:text-accent-400">
            {t('admin.tipQuoteInstructions')}
          </p>
        </div>

        {/* Save Button */}
        <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                {t('admin.saving')}
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {t('admin.saveSettings')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

